import React, { useRef, useState } from 'react';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Select, Popconfirm, Row, InputRef, Tooltip } from 'antd';

const { Option } = Select;

interface ISelectWithAddProps {
	items: IItem[],
	addItem: (item: string) => Promise<void>,
	deleteItem: (item: string) => Promise<void>,
	placeholder: string,
	setValue: React.Dispatch<string>,
	currentSelectedTilePreset?: string,
	addItemLabel: string,
	addItemTextboxLabel: string,
	label: string,
	labelPaddingRight?: number,
	allowClear?: boolean
}

export const SelectWithAdd = ({
	items,
	addItem,
	deleteItem,
	placeholder,
	setValue,
	currentSelectedTilePreset,
	addItemLabel,
	addItemTextboxLabel,
	label,
	labelPaddingRight,
	allowClear = false
}: ISelectWithAddProps) => {
	const [name, setName] = useState<string>('');
	const inputRef: React.RefObject<InputRef | null> = useRef(null);
	const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setName(event.target.value);
	};
	const addItemLocal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();
		addItem(name)
		setName('');
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	const isAddButtonDisabled = (): boolean => {
		return name.trim() === '' || items.some((e) => e.label === name)
	}

	return (
		<Row style={{ alignItems: 'baseline' }}>
			{label &&
				<p style={{ paddingRight: `${labelPaddingRight || 4}px` }}>{label}</p>
			}
			<Select
				value={currentSelectedTilePreset}
				onChange={(value) => setValue(value)}
				placeholder={placeholder}
				defaultValue={currentSelectedTilePreset !== undefined ? items[0]?.value : undefined}
				style={{
					flexGrow: 1
				}}
				allowClear={allowClear}
				dropdownRender={(menu) => {
					return (
						<>
							{menu}
							<Divider
								style={{
									margin: '8px 0',
								}}
							/>
							<div
								style={{
									padding: '0 8px 4px',
									width: '100%',
									display: 'flex'
								}}
							>
								<Input
									placeholder={addItemTextboxLabel}
									ref={inputRef}
									value={name}
									onChange={onNameChange}
									onKeyDown={(e) => e.stopPropagation()}
								/>
								<Tooltip title={isAddButtonDisabled() && 'Please enter valid preset name'}>
									<Button type="text" icon={<PlusOutlined />} onClick={addItemLocal} disabled={isAddButtonDisabled()}>
										{addItemLabel}
									</Button>
								</Tooltip>
							</div>
						</>
					)
				}}
			>
				{items.map((item: IItem) => (
					<Option key={item.value} value={item.value}>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
							}}
						>
							<span>{item.label}</span>
							{/* Any staff user can select a preset. Only its creator can delete it. */}
							{(item.deletable ?? !item.disabled) && (
								<Popconfirm
									title="Are you sure to delete?"
									onConfirm={() => deleteItem(item.value)}
									okText="Yes"
									cancelText="No"
								>
									<Button
										type="link"
										icon={<DeleteOutlined style={{ color: 'red' }} />}
										style={{ padding: 0 }}
									/>
								</Popconfirm>
							)}
						</div>
					</Option>
				))}
			</Select>
		</Row>
	);
};

interface IItem {
	label: string;
	value: string;
	disabled: boolean;
	/** Shows the delete icon. Without it, the icon shows on every item that is not disabled. */
	deletable?: boolean;
}